import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ActivityForm from '../../../components/Forms/ActivityForm';
import { activityService } from '../../../services/apiService';
import { formatDate } from '../../../utils/dateFormat';
import { showToast } from '../../../utils/toast';

const ActivitiesTab = ({ onAddRef }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); 
        setError(null);
        const res = await activityService.getAll();
        const activities = res?.data || res || [];
        setActivities(Array.isArray(activities) ? activities : []);
      } catch (err) {
        console.error('Error loading activities:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to load activities');
      } finally { 
        setLoading(false); 
      }
    })();
  }, []);

  const refresh = async () => {
    try {
      const res = await activityService.getAll();
      const activities = res?.data || res || [];
      setActivities(Array.isArray(activities) ? activities : []);
    } catch (err) {
      console.error('Error refreshing activities:', err);
      showToast.error(err?.response?.data?.error || err?.message || 'Failed to refresh activities');
    }
  };

  const handleAdd = useCallback(() => { setEditing(null); setModalOpen(true); }, []);

  useEffect(() => {
    if (onAddRef) {
      onAddRef(handleAdd);
    }
  }, [handleAdd, onAddRef]);
  const handleEdit = (row) => { setEditing(row); setModalOpen(true); };
  const handleModalClose = () => { setModalOpen(false); setEditing(null); };
  const handleModalSave = async (formData) => {
    try {
      setModalLoading(true);
      if (editing) { 
        await activityService.update(editing.id, formData); 
        showToast.success('Activity updated'); 
      } else { 
        await activityService.create(formData); 
        showToast.success('Activity created'); 
      }
      await refresh();
      handleModalClose();
    } catch (err) { 
      showToast.error(err?.response?.data?.message || err?.message || 'Action failed'); 
    } finally { 
      setModalLoading(false); 
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('Delete this activity?')) return;
    try { 
      await activityService.delete(row.id); 
      showToast.success('Activity deleted'); 
      await refresh(); 
    } catch (err) { 
      showToast.error(err?.response?.data?.message || err?.message || 'Delete failed'); 
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No activities found
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: { xs: 1.5, sm: 1.75 },
          }}
        >
          {activities.map((activity) => (
            <Paper
              key={activity.id}
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.4 },
                borderRadius: 2,
                borderColor: 'divider',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" fontWeight={700}>
                      {activity.name || '—'}
                    </Typography>
                    <Chip
                      size="small"
                      label={(activity.type || '-').charAt(0).toUpperCase() + (activity.type || '-').slice(1)}
                      color={activity.type === 'holiday' ? 'success' : 'info'}
                      variant="outlined"
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Date:</strong> {formatDate(activity.date)}
                    </Typography>
                  </Stack>
                  {activity.description && (
                    <Typography variant="body2" color="text.secondary">
                      {activity.description}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(activity)}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(activity)}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.dark',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      <ActivityForm
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSave}
        initialData={editing}
        loading={modalLoading}
        title={editing ? 'Edit Activity' : 'Add Activity'}
      />
    </Box>
  );
};

export default ActivitiesTab;
